import React from 'react';
import {List} from 'immutable';
import StatTabContent from 'components/dashboard/StatTabContent';
import Loading from 'components/pure/utils/Loading';
import RepProfilePageActions from 'actions/RepProfilePageActions';
import {
  makeChartLabel,
  TOPIC_ROLLUP_BAR_COLOR,
  TOPIC_ROLLUP_SUBTABS,
} from 'components/dashboard/rollups/topic/common';
import SelectField from 'components/pure/utils/form/SelectField';
import RepProfileTrendChartContainer from '$pages/@common/rep-profile-trend-chart/RepProfileTrendChartContainer';
import './TopicRollupTeamTab.styl';
import {MetricType} from 'sources/dashboard/DashMetricsSource';
import * as queryParams from 'utils/queryParams';

/**
 * Renders a team tab in the topic rollup.
 */
export default class TopicRollupTeamTab extends React.PureComponent {
  constructor(props) {
    super(props);
    // Fetch a default triggerId from router state here if it exists
    this.state = {
      topicDropdownOptions: List(),
    };
  }

  topicDropdownOptions() {
    const {stats, triggerNameById} = this.props;
    if (!stats) {
      return List();
    }
    return (
      stats
        // get triggerIds from stats in both subtabs. They should have the same set,
        // but there's no reason to pick one over the other. Might as well just plow through
        // everything.
        .valueSeq()
        .flatten(1)
        .map(stat => stat.context)
        .toSet() // Remove duplicates.
        .map(triggerId => {
          const label = triggerNameById(triggerId);
          if (!label) {
            // Failsafe for bad filtering. Metrics should contain no triggerIds for inactive triggers
            // by this point, and should otherwise be filtered down to those relevant for display.
            console.warn(`No label for triggerId ${triggerId}`);
            return null;
          }
          return {
            label,
            value: triggerId,
          };
        })
        .filter(opt => opt) // remove nulls.
        .sortBy(opt => opt.label.toLowerCase())
    );
  }

  componentDidMount() {
    const {triggerId} = this.props;
    const topicDropdownOptions = this.topicDropdownOptions();
    this.setState({topicDropdownOptions});

    if (!triggerId) {
      const first = topicDropdownOptions.first();
      const newTriggerId = first && first.value;
      queryParams.set({triggerId: newTriggerId});
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const {stats, triggerId} = this.props;
    const {stats: prevStats} = prevProps;

    if (stats !== prevStats) {
      const topicDropdownOptions = this.topicDropdownOptions();
      this.setState({topicDropdownOptions});

      const first = topicDropdownOptions.first();
      // Double-check that the selected option is still valid.
      const newTriggerId = topicDropdownOptions.some(o => o.value === triggerId)
        ? triggerId
        : first && first.value;
      queryParams.set({triggerId: newTriggerId});
    }
  }

  render() {
    const {
      activeSubtabConfig,
      activeSubtabId,
      activeTabId,
      endDate,
      error,
      headerStartDate,
      headerEndDate,
      loading,
      location,
      onSelectSubtab,
      startDate,
      stats,
      tabConfigs,
      users,
      triggerNameById,
      triggerId,
      callDrillDown,
    } = this.props;

    if (loading) {
      return (
        <div className="rep-stat-tab-content">
          <Loading />
        </div>
      );
    }
    if (error) {
      return <div className="rep-stat-tab-content">Problem loading the data.</div>;
    }

    const {topicDropdownOptions} = this.state;
    const filteredStats = stats
      .get(activeSubtabId, List())
      .filter(stat => stat.context === triggerId);

    const getLinkAndName = stat => {
      const user = users
        .valueSeq()
        .filter(user => user.userId === stat.id)
        .first();
      return user ? [user.getFullName(), user.getFullName()] : [null, null];
    };

    const fetchTrendData = stat => {
      if (activeSubtabId === TOPIC_ROLLUP_SUBTABS.PERCENT) {
        RepProfilePageActions.getTopicPercentageTrend({
          userId: stat.id,
          startDate,
          endDate,
          context: triggerId,
        });
      } else {
        RepProfilePageActions.getTrendData({
          userId: stat.id,
          startDate,
          endDate,
          context: triggerId,
          type: MetricType.trigger,
        });
      }
    };

    // StatTabContent renders this in a div with class "description".
    const description = (
      <div className="description-and-dropdown">
        <div>{activeSubtabConfig.description}</div>
        <div className="dropdown">
          {topicDropdownOptions.isEmpty() || (
            <SelectField
              hideResetButton
              onChange={triggerId => queryParams.set({triggerId})}
              options={topicDropdownOptions}
              value={triggerId}
            />
          )}
        </div>
      </div>
    );

    const descriptions = {};
    descriptions[activeSubtabId] = description;

    const triggerName = triggerNameById(triggerId);

    return (
      <div className="topic-rollup-team-tab">
        <StatTabContent
          activeSubtabId={activeSubtabId}
          activeTabId={activeTabId}
          chartLabelFn={(...args) => makeChartLabel(...args) + ` - ${triggerName}`}
          color={TOPIC_ROLLUP_BAR_COLOR}
          descriptions={descriptions}
          endDate={endDate}
          fetchTrendData={fetchTrendData}
          getLinkAndName={getLinkAndName}
          headerStartDate={headerStartDate}
          headerEndDate={headerEndDate}
          location={location}
          selectSubtab={onSelectSubtab}
          startDate={startDate}
          stats={filteredStats}
          tabConfigs={tabConfigs}
          trendComponent={RepProfileTrendChartContainer}
          onOpenDrillDown={callDrillDown}
        />
      </div>
    );
  }
}
